import React, { type FC, useContext } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';

import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE,
	IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { EmptyStateWrapper } from './EmptyStateWrapper.tsx';
import errorImage from './Error.svg';

const i18n = defineMessages({
	header: {
		id: 'template-list.error.headline',
		defaultMessage: 'We couldn’t load your templates',
		description:
			'This is the headline that shows when we can’t display the list available templates, typically due to a network or API error',
	},
	description: {
		id: 'template-list.error.description',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We're having trouble loading templates right now.{lineBreak} Refresh the page to try again.",
		description:
			'This is the description that shows when we can’t display the list available templates, typically due to a network or API error',
	},
	spaceErrorHeader: {
		id: 'template-list.space-error.header',
		defaultMessage: 'Unable to load templates',
		description:
			'This is the headline that shows when we can’t display the list available templates, due to a particular error of a space not being found',
	},
	spaceErrorDescription: {
		id: 'template-list.space-error.description',
		defaultMessage:
			'This space may no longer exist, be archived, or you don’t have permissions to see it.{lineBreak} Check archives or reach out to your space admin to restore your access.',
		description:
			'This is the description that shows when we can’t display the list available templates, due to a particular error of a space not being found',
	},
});

type ErrorTemplateListProps = {
	selectedSpaceError: ApolloErrorInterop;
	categoriesError?: ApolloErrorInterop;
};

enum ErrorType {
	DEFAULT,
	UNAUTHORIZED,
	TOO_MANY_REQUESTS,
	INVALID_SPACE,
}

const lineBreak = { lineBreak: <br /> };
const ErrorTemplateListComponent: FC<ErrorTemplateListProps & WrappedComponentProps> = ({
	intl,
	selectedSpaceError,
	categoriesError,
}) => {
	const getErrorType = (error: ApolloErrorInterop): ErrorType => {
		const statusCode = getStatusCode(error?.graphQLErrors?.[0]);
		const isSpaceNotFound =
			error?.graphQLErrors?.[0]?.message.toLowerCase().includes('invalid spacekeys') &&
			statusCode === '404';

		if (isSpaceNotFound) {
			return ErrorType.INVALID_SPACE;
		}
		return ErrorType.DEFAULT;
	};

	const errorType = getErrorType(selectedSpaceError || categoriesError);

	const getHeaderMessage = () => {
		if (errorType === ErrorType.INVALID_SPACE) {
			return intl.formatMessage(i18n.spaceErrorHeader);
		} else {
			return intl.formatMessage(i18n.header);
		}
	};

	const getDescriptionMessage = () => {
		if (errorType === ErrorType.INVALID_SPACE) {
			return <FormattedMessage {...i18n.spaceErrorDescription} values={lineBreak} />;
		} else {
			return <FormattedMessage {...i18n.description} values={lineBreak} />;
		}
	};

	const experienceTracker = useContext(ExperienceTrackerContext);

	if (errorType === ErrorType.INVALID_SPACE) {
		markErrorAsHandled(selectedSpaceError);
		markErrorAsHandled(categoriesError);
		experienceTracker.succeed({
			name: IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE,
		});
		experienceTracker.succeed({
			name: IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE,
		});
	} else {
		experienceTracker.fail({
			name: IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE,
			error: selectedSpaceError,
		});
		experienceTracker.fail({
			name: IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE,
			error: categoriesError || selectedSpaceError,
		});
	}

	return (
		<EmptyStateWrapper
			header={getHeaderMessage()}
			description={getDescriptionMessage()}
			imageUrl={errorImage}
			imageHeight={109}
		/>
	);
};

export const ErrorTemplateList = injectIntl(ErrorTemplateListComponent);
